import { getTokens } from './apiUtils';
import { getCachedApiUrl } from './apiConfig';

export interface GlobalChatUpdate {
  event: 'chat_created' | 'chat_updated' | 'chat_deleted' | 'message_added' | 'heartbeat';
  data: any;
  timestamp: string;
}

/**
 * Конвертирует Python dictionary формат в валидный JSON
 * Правильно обрабатывает вложенный JSON и экранирует кавычки
 */
function convertPythonDictToJson(pythonDict: string): string {
  try {
    // Заменяем Python литералы на JSON
    let result = pythonDict
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')  
      .replace(/\bNone\b/g, 'null');
    
    // Заменяем одинарные кавычки на двойные, правильно экранируя содержимое
    const tokens: Array<{start: number, end: number, replacement: string}> = [];
    let inString = false;
    let stringStart = -1;
    let stringChar = '';
    let i = 0;
    
    while (i < result.length) {
      const char = result[i];
      const prevChar = i > 0 ? result[i-1] : '';
      
      // Пропускаем экранированные символы
      if (prevChar === '\\') {
        i++;
        continue;
      }
      
      if (!inString && char === "'") {
        inString = true;
        stringStart = i;
        stringChar = char;
      } else if (inString && char === stringChar) {
        // Найдена закрывающая кавычка
        const stringContent = result.substring(stringStart + 1, i);
        const escapedContent = stringContent.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        tokens.push({
          start: stringStart,
          end: i + 1,
          replacement: `"${escapedContent}"`
        });
        inString = false;
      }
      
      i++;
    }
    
    // Применяем замены в обратном порядке
    for (let j = tokens.length - 1; j >= 0; j--) {
      const token = tokens[j];
      result = result.substring(0, token.start) + token.replacement + result.substring(token.end);
    }
    
    return result;
  } catch (error) {
    console.error('Ошибка конвертации Python dict:', error);
    return pythonDict;
  }
}

export class GlobalChatStreamService {
  private eventSource: EventSource | null = null;
  private onChatUpdateCallback: ((update: GlobalChatUpdate) => void) | null = null;
  private onErrorCallback: ((error: any) => void) | null = null;
  private onHeartbeatCallback: (() => void) | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // 1 секунда

  constructor(
    onChatUpdate: (update: GlobalChatUpdate) => void,
    onError?: (error: any) => void,
    onHeartbeat?: () => void
  ) {
    this.onChatUpdateCallback = onChatUpdate;
    this.onErrorCallback = onError || null;
    this.onHeartbeatCallback = onHeartbeat || null;
  }

  // Начать прослушивание глобальных обновлений
  start(): void {
    if (this.eventSource) {
      this.stop();
    }

    const { accessToken } = getTokens();
    if (!accessToken) {
      console.warn('Нет токена доступа для подключения к глобальному потоку');
      return;
    }

    // Создаем URL для SSE с токеном
    const baseUrl = getCachedApiUrl();
    const streamUrl = `${baseUrl}/api/chats/global-stream?token=${accessToken}`;

    this.eventSource = new EventSource(streamUrl);

    this.eventSource.onopen = () => {
      console.log('Глобальный поток чатов подключен');
      this.reconnectAttempts = 0;
    };

    this.eventSource.onmessage = (event) => {
      try {
        // Логируем сырые данные для отладки
        console.log('Global SSE raw data:', event.data);
        
        // Проверяем, что данные не пустые
        if (!event.data || event.data.trim() === '') {
          console.warn('Получены пустые данные глобального SSE');
          return;
        }

        // Проверяем, что данные начинаются с '{' или '['
        const trimmedData = event.data.trim();
        if (!trimmedData.startsWith('{') && !trimmedData.startsWith('[')) {
          console.warn('Некорректный формат JSON данных в глобальном потоке:', trimmedData);
          return;
        }
        
        let update: GlobalChatUpdate;
        try {
          // Сначала пытаемся парсить как есть
          update = JSON.parse(trimmedData);
        } catch (parseError) {
          // Если не получилось, пытаемся конвертировать Python dict в JSON
          try {
            console.log('Пытаемся конвертировать Python dictionary в JSON (глобальный поток)...');
            const jsonData = convertPythonDictToJson(trimmedData);
            console.log('Конвертированные данные (глобальный поток):', jsonData);
            update = JSON.parse(jsonData);
            console.log('Успешно спарсен конвертированный JSON (глобальный поток)');
          } catch (conversionError) {
            console.error('Ошибка парсинга JSON в глобальном потоке:', parseError);
            console.error('Ошибка конвертации Python dict (глобальный поток):', conversionError);
            console.error('Проблемные данные:', trimmedData);
            console.error('Длина данных:', trimmedData.length);
            console.error('Первые 100 символов:', trimmedData.substring(0, 100));
            return;
          }
        }
        
        switch (update.event) {
          case 'chat_created':
          case 'chat_updated':
          case 'chat_deleted':
          case 'message_added':
            if (this.onChatUpdateCallback) {
              this.onChatUpdateCallback(update);
            }
            break;
          case 'heartbeat':
            if (this.onHeartbeatCallback) {
              this.onHeartbeatCallback();
            }
            break;
          default:
            console.warn('Неизвестное событие в глобальном потоке:', update.event);
        }
      } catch (error) {
        console.error('Общая ошибка обработки глобального SSE сообщения:', error);
        console.error('Сырые данные события:', event.data);
        console.error('Тип события:', event.type);
        
        // Не вызываем onErrorCallback для ошибок парсинга JSON,
        // так как это может быть временная проблема с форматом данных
        if (error instanceof SyntaxError && error.message.includes('JSON')) {
          console.warn('Пропускаем некорректное JSON сообщение в глобальном потоке, поток продолжает работать');
        } else if (this.onErrorCallback) {
          this.onErrorCallback(error);
        }
      }
    };

    this.eventSource.onerror = (error) => {
      console.error('Ошибка глобального потока чатов:', error);
      
      if (this.onErrorCallback) {
        this.onErrorCallback(error);
      }

      // Автоматическое переподключение
      this.handleReconnect();
    };
  }

  // Остановить прослушивание
  stop(): void {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.reconnectAttempts = 0;
  }

  // Проверить, подключен ли поток
  isConnected(): boolean {
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  // Обработка переподключения
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Превышено максимальное количество попыток переподключения');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Экспоненциальная задержка

    console.log(`Попытка переподключения ${this.reconnectAttempts}/${this.maxReconnectAttempts} через ${delay}мс`);

    setTimeout(() => {
      if (this.eventSource?.readyState === EventSource.CLOSED) {
        this.start();
      }
    }, delay);
  }

  // Обновить токен и переподключиться
  updateToken(): void {
    if (this.isConnected()) {
      this.stop();
      this.start();
    }
  }
}
